import { useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import {
  EVENT_DRAFT,
  EVENT_STATUSES,
  LANGUAGE_EN,
  LANGUAGE_PL,
} from '../../lib/constants';

// Reusable create-event modal. Hosts: Dashboard "+ New Event" button, Upload page.
// Props:
//   onClose():   close without creating
//   onCreated(event): called with the created event before the modal closes
export function CreateEventModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [location, setLocation] = useState('');
  const [language, setLanguage] = useState(LANGUAGE_PL);
  const [status, setStatus] = useState(EVENT_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !submitting;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await api.events.create({
        name: trimmedName,
        eventDate: eventDate || null,
        location: location.trim() || null,
        language,
        status,
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setError(err?.message || t('events.create.error'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleBackdropClick() {
    if (!submitting) onClose();
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={handleBackdropClick}>
      <form
        className="modal modal--form"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="modal__title">{t('events.create.title')}</h2>

        <label className="form-field">
          <span className="form-field__label">{t('events.create.name')} *</span>
          <input
            className="form-field__input"
            type="text"
            value={name}
            placeholder={t('events.create.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
        </label>

        <div className="modal__row">
          <label className="form-field">
            <span className="form-field__label">{t('events.create.date')}</span>
            <input
              className="form-field__input"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-field__label">{t('events.create.location')}</span>
            <input
              className="form-field__input"
              type="text"
              value={location}
              placeholder={t('events.create.locationPlaceholder')}
              onChange={(e) => setLocation(e.target.value)}
            />
          </label>
        </div>

        <div className="form-field">
          <span className="form-field__label">{t('events.create.language')}</span>
          <div className="radio-group">
            {[LANGUAGE_PL, LANGUAGE_EN].map((lang) => (
              <label key={lang}>
                <input
                  type="radio"
                  name="create-event-language"
                  checked={language === lang}
                  onChange={() => setLanguage(lang)}
                />
                {lang === LANGUAGE_PL ? '🇵🇱 Polski' : '🇬🇧 English'}
              </label>
            ))}
          </div>
        </div>

        <label className="form-field">
          <span className="form-field__label">{t('events.create.status')}</span>
          <select
            className="form-field__select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {EVENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`events.statuses.${s}`)}
              </option>
            ))}
          </select>
        </label>

        {error && <div className="modal__error" role="alert">{error}</div>}

        <div className="modal__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={!canSubmit}>
            {submitting ? t('events.create.submitting') : t('events.create.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
